import { useCallback, useEffect, useMemo, useState } from 'react'
import QRCode from 'react-qr-code'
import find from 'lodash/find'
import { useFormikContext } from 'formik'
import ClassNames from 'classnames'

import { formatCurrencyAmountFromConfig, tokenizer } from '../../../services/utils'
import { CryptoPaymentDropdown } from '../CryptoPaymentDropdown'
import Dropdown from '../../Dropdown/Dropdown'
import { config } from '../../Init/Init'
import l10n from '../../i18n/I18N'
import { useCryptoPaymentDataModal } from '../CryptoPaymentModalDataProvider'
import { CryptoPaymentDepositCurrencyIcon } from './CryptoPaymentDepositCurrencyIcon'
import { CryptoPaymentInput } from '../CryptoPaymentFormElements/CryptoPaymentInput'
import { CryptoPaymentDepositSkeleton } from './CryptoPaymentDepositSkeleton'
import { CryptoPaymentDepositInfo } from './CryptoPaymentDepositInfo'
import { useGetCryptoChannel, useGetCryptoCurrencyExchangeRate, useOnCoinsAmountChange } from './hooks'
import { CryptoPaymentFormGroup } from '../CryptoPaymentFormElements/CryptoPaymentFormGroup'
import { depositFormFields, formFieldPlaceholders, qrCodeSize } from '../cryptoPaymentConstants'
import { inputActionType } from '../CryptoPaymentFormElements/cryptoPaymentInputConstants'

import classes from './styles/cryptoPaymentDeposit.module.scss'
import { ReactComponent as CrossArrows } from '../../../static/images/icons/cross-arrows.svg'

export const CryptoPaymentDepositFormContent = () => {
  const [protocol, setProtocol] = useState(null)

  const { loading, setLoading } = useCryptoPaymentDataModal()
  const {
    isXRPCryptoType,
    selectedCurrency,
    cryptoCurrencies,
    setSelectedCurrency,
    isCryptoCurrenciesLoading
  } = useCryptoPaymentDataModal()

  const { setFieldValue, values } = useFormikContext()
  const { getCryptoCurrencyExchangeRate, isExchangeRateLoading, exchangeRate } = useGetCryptoCurrencyExchangeRate()
  const { onCoinsAmountChange, cryptoCurrencyConfigs, updatedSectionName } = useOnCoinsAmountChange(exchangeRate)
  const { getCryptoChannel, getChannelError, cryptoChannelData, setGetChannelError } = useGetCryptoChannel({
    setLoading,
    getCryptoCurrencyExchangeRate
  })

  const getDepositAddress = useCallback(() => {
    let cryptoAddress = cryptoChannelData.depositAddress

    if (protocol) {
      const alternative = find(cryptoChannelData.alternatives, { protocol: protocol })

      if (alternative && alternative.address) {
        cryptoAddress = alternative.address
      }
    }

    return cryptoAddress
  }, [cryptoChannelData.alternatives, cryptoChannelData.depositAddress, protocol])

  useEffect(() => {
    if (cryptoCurrencies.length !== 0 && !values[depositFormFields.cryptoCurrencyCode]) {
      setFieldValue(depositFormFields.cryptoCurrencyCode, selectedCurrency.currencyCode)
    }

    setFieldValue(depositFormFields.destinationTag, cryptoChannelData.destinationTag)
    setFieldValue(depositFormFields.address, getDepositAddress())
  }, [selectedCurrency.currencyCode, setFieldValue, values, getDepositAddress])

  const DepositAmountLabel = () => {
    return (
      <>
        {tokenizer(l10n.t['cryptopayment.deposit.cryptoAmount.label'], {
          '{currency}': values[depositFormFields.cryptoCurrencyCode]
        })}
        &nbsp;
        <CryptoPaymentDepositCurrencyIcon currencyCode={values[depositFormFields.cryptoCurrencyCode]} />
        &nbsp;
        {l10n.t['cryptopayment.deposit.cryptoAmountAddress.label']}
      </>
    )
  }

  const onCurrencyChange = useCallback(
    currency => {
      setGetChannelError('')
      setLoading(true)
      setSelectedCurrency(currency)
      getCryptoChannel(currency)
      setProtocol(null)
      getCryptoCurrencyExchangeRate({ from: config.baseFiatCurrency, to: currency.currencyCode })
    },
    [setGetChannelError, setLoading, setSelectedCurrency, getCryptoChannel, getCryptoCurrencyExchangeRate]
  )

  const isDataLoading = useMemo(() => loading || isCryptoCurrenciesLoading || isExchangeRateLoading, [
    loading,
    isExchangeRateLoading,
    isCryptoCurrenciesLoading
  ])

  const sectionClassName = ClassNames(classes.cryptoPaymentDeposit__section, {
    [classes.cryptoPaymentDeposit__section_active]: updatedSectionName === depositFormFields.cryptoAmount
  })

  const crossArrowsClassName = ClassNames(classes.cryptoPaymentDeposit__crossArrowsIcon, {
    [classes.cryptoPaymentDeposit__crossArrowsIcon_active]: updatedSectionName
  })

  return (
    <>
      <CryptoPaymentDepositInfo onCoinsAmountChange={onCoinsAmountChange} updatedSectionName={updatedSectionName} />
      <div className={classes.cryptoPaymentDeposit__crossArrows}>
        <CrossArrows className={crossArrowsClassName} />
      </div>
      {!config.useBvnkHostedPage && (
        <>
          {!isDataLoading && (
            <>
              <div className={sectionClassName}>
                <CryptoPaymentFormGroup>
                  <CryptoPaymentDropdown
                    items={cryptoCurrencies}
                    label={DepositAmountLabel}
                    onChange={onCurrencyChange}
                    className="formElement"
                    disabled={loading || isCryptoCurrenciesLoading}
                    name={depositFormFields.cryptoCurrencyCode}
                    inputName={depositFormFields.cryptoAmount}
                    inputPlaceholder={formFieldPlaceholders.crypto}
                    onInputChange={value => onCoinsAmountChange(value, depositFormFields.coinsAmount)}
                    inputFormat={value => {
                      if (isNaN(value) || value <= 0) return formatCurrencyAmountFromConfig(0, cryptoCurrencyConfigs)

                      return formatCurrencyAmountFromConfig(value, cryptoCurrencyConfigs)
                    }}
                  />
                  {cryptoChannelData.alternatives && cryptoChannelData.alternatives.length !== 0 && (
                    <Dropdown
                      className="formElement"
                      disabled={loading}
                      selectedItem={protocol || cryptoChannelData.alternatives[0].protocol}
                      items={Array.from(cryptoChannelData.alternatives, ({ protocol }) => protocol)}
                      onChange={protocol => setProtocol(protocol)}
                    />
                  )}
                  {getChannelError && <div className={classes.cryptoPaymentDeposit__error}>{getChannelError}</div>}
                  {!getChannelError && (
                    <>
                      {isXRPCryptoType && (
                        <CryptoPaymentInput
                          readOnly
                          name={depositFormFields.destinationTag}
                          label={l10n.t['cryptopayment.deposit.tag.label']}
                        />
                      )}
                      <CryptoPaymentInput
                        label={() => (
                          <>
                            {tokenizer(l10n.t['cryptopayment.deposit.address.label'], {
                              '{currency}': values[depositFormFields.cryptoCurrencyCode]
                            })}
                          </>
                        )}
                        actionType={inputActionType.copy}
                        readOnly
                        name={depositFormFields.address}
                      />
                      <div className={classes.cryptoPaymentDeposit__qrCode}>
                        <QRCode
                          value={getDepositAddress()}
                          size={qrCodeSize}
                          fgColor={classes.cryptoPaymentQrCodeColor}
                          bgColor={classes.cryptoPaymentQRbackColor}
                          style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                          viewBox={`0 0 ${qrCodeSize} ${qrCodeSize}`}
                        />
                      </div>
                      <div className={classes.cryptoPaymentDeposit__addressDescription}>
                        <div>
                          {tokenizer(l10n.t['cryptopayment.deposit.currencyNotification'], {
                            '{currency}': values[depositFormFields.cryptoCurrencyCode]
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </CryptoPaymentFormGroup>
              </div>
            </>
          )}
          {isDataLoading && <CryptoPaymentDepositSkeleton isXRPCryptoType={isXRPCryptoType} />}
        </>
      )}

      {config.useBvnkHostedPage &&
        !isDataLoading &&
        !getChannelError &&
        values[depositFormFields.cryptoCurrencyCode] && (
          <iframe
            className={classes.cryptoPaymentDeposit__iframe}
            src={cryptoChannelData.bvnkHostedPageUrl}
            id="crypto-payment-iframe"
          />
        )}
    </>
  )
}
